import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const stats = [
    { value: '500+', label: '综艺节目', icon: 'ri-movie-2-line' },
    { value: '1000万+', label: '月活跃用户', icon: 'ri-user-star-line' },
    { value: '99.9%', label: '播放成功率', icon: 'ri-shield-check-line' },
    { value: '24/7', label: '内容更新', icon: 'ri-refresh-line' },
  ];

  const testimonials = [
    {
      name: '小王',
      role: '综艺爱好者',
      content: '极速影院真的是我发现的最好的综艺网站！更新快、画质好，再也不用到处找资源了。',
      rating: 5,
    },
    {
      name: '李女士',
      role: '全职妈妈',
      content: '带娃之余就喜欢看综艺放松一下，极速影院分类很全，想看什么类型都有，推荐！',
      rating: 5,
    },
    {
      name: '张同学',
      role: '大学生',
      content: '和室友一起追综艺，极速影院的加载速度超快，宿舍网也不卡，好评！',
      rating: 5,
    },
  ];

  const faqItems = [
    {
      q: '极速影院收费吗？',
      a: '极速影院是完全免费的综艺节目在线观看平台，我们致力于为用户提供免费、高清、流畅的综艺娱乐内容。所有节目均可免费观看，无需注册或付费。',
    },
    {
      q: '需要下载APP吗？',
      a: '不需要！极速影院是纯网页版平台，无需下载任何APP或插件。您只需使用浏览器访问我们的网站，即可随时随地观看综艺节目。',
    },
    {
      q: '为什么播放不卡顿？',
      a: '我们采用了先进的CDN加速技术和智能码率适配系统，能够根据您的网络状况自动调整画质，确保流畅的观看体验。同时，我们持续优化服务器性能，为用户提供极速加载体验。',
    },
    {
      q: '综艺节目更新频率如何？',
      a: '我们的内容团队全天候关注各大卫视和视频平台的最新综艺节目，确保第一时间同步更新。热门综艺通常在播出后24小时内即可在极速影院观看。',
    },
    {
      q: '支持哪些设备观看？',
      a: '极速影院支持所有主流设备，包括电脑、平板和手机。我们的网站采用响应式设计，能够自适应各种屏幕尺寸，为您提供最佳的观看体验。',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    setFormSubmitting(true);

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9nftpgbdkpk99j7gj20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    } finally {
      setFormSubmitting(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '极速影院',
    url: 'https://www.jisucinema.com',
    description: '极速影院是一个免费综艺节目在线观看平台，提供高清流畅的综艺娱乐内容。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jisucinema.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Hero */}
          <section className="w-full px-4 md:px-6 pt-8 pb-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <div className="text-center mt-4 mb-4">
                <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 mb-4">
                  关于极速影院
                </h1>
                <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  极速影院是一个专注于提供<strong>免费综艺节目在线观看</strong>的平台。我们致力于为每一位观众带来
                  <strong>高清不缓冲</strong>的极致观影体验，让您在忙碌的生活中，随时随地享受精彩的综艺娱乐内容。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 rounded-lg bg-background-50"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${stat.icon} text-xl text-primary-500`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                    我们的故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      极速影院诞生于一群综艺爱好者的热情之中。我们发现，很多观众在寻找综艺节目时，常常面临画质差、加载慢、资源不全等问题。于是，我们决定打造一个真正以用户体验为核心的<strong>免费综艺平台</strong>。
                    </p>
                    <p>
                      经过多年的积累和优化，极速影院已经发展成为国内领先的<strong>综艺节目在线观看</strong>平台，收录了超过<strong>500档</strong>热门综艺节目，覆盖音乐、户外、美食、脱口秀等多种类型。
                    </p>
                    <p>
                      我们始终坚持<strong>"高清不缓冲"</strong>的核心承诺，不断优化技术架构，让每一位用户都能享受到流畅的观看体验。从综艺大全到最新综艺，极速影院始终与观众同行。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden h-72 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=Chinese%20modern%20office%20team%20working%20on%20entertainment%20content%20creation%2C%20collaborative%20atmosphere%2C%20warm%20lighting%2C%20creative%20studio%20environment%2C%20multiple%20screens%20displaying%20colorful%20content%2C%20clean%20minimalist%20design&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="极速影院团队"
                    title="极速影院团队 - 免费综艺节目在线观看"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                来自真实用户的评价
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="p-5 rounded-lg bg-background-50">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary-500">
                          {t.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-800">
                          {t.name}
                        </div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                关于极速影院的常见疑问解答
              </p>
              <div className="space-y-3">
                {faqItems.map((item, idx) => (
                  <details
                    key={idx}
                    className="group rounded-lg bg-background-100 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between p-4 text-sm font-semibold text-foreground-800 list-none">
                      {item.q}
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                有任何问题或建议，欢迎与我们联系
              </p>

              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-check-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">
                    感谢您的留言！
                  </h3>
                  <p className="text-sm text-foreground-600">
                    我们已收到您的信息，会尽快与您联系。
                  </p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  {formError && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      姓名
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="form-anti-spam"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formSubmitting ? '提交中...' : '发送留言'}
                  </button>
                </form>
              )}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">邮箱</div>
                    <div className="text-sm font-medium text-foreground-800">
                      contact@jisucinema.com
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-phone-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">电话</div>
                    <div className="text-sm font-medium text-foreground-800">
                      400-888-9999
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-time-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">工作时间</div>
                    <div className="text-sm font-medium text-foreground-800">
                      周一至周五 9:00-18:00
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}